import { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';

import { Page } from '@occc-app/design-system';
import { Tabs, Tab } from '@occc/ui-design-system';
import { DateRangeSelector, getDateRanges } from '@occc/ui-design-system';
import { useConfig } from '@occc/config';
import { DateTimeFormat, formatDateTime, isSameDateTime } from '@occc-app/lib/date';

import { PerformanceTabItem, TabValue } from './retailer-network-performance-page.types';
import {
  LOYALTY_PROGRAM_TAB,
  LOYALTY_PROGRAM_TAB_PATH,
  TOBACCO_PROGRAM_TAB,
  TOBACCO_PROGRAM_TAB_PATH,
} from './retailer-network-performance-page.constants';
import { RetailerNetworkLoyaltyPerformanceContainer } from './components/retailer-network-loyalty-performance.container';
import { RetailerNetworkTobaccoPerformanceController } from './components/retailer-network-tobacco-performance.controller';

import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { useTransactionSetSelection } from '../hooks/use-transaction-set-selection.hook';
import { TransactionSetSelector } from '../components/transaction-set-selector';

const PAGE_TITLE = 'Program Performance';
const BREADCRUMB_LINKS = [{ label: PAGE_TITLE }];

export const RetailerNetworkPerformancePage = ({ retailerNetwork }: WithRetailerNetworkProps) => {
  const isTobaccoPerformanceEnabled = useConfig('retailerConnectTobaccoPerformance.enabled');
  const isMobilePayMetricsEnabled = useConfig('retailerConnectMobilePayMetrics.enabled');

  useBreadcrumbLinks(BREADCRUMB_LINKS);
  const location = useLocation();
  const periods = useMemo(() => {
    const dateRanges = getDateRanges();
    return {
      presets: [
        dateRanges.today,
        dateRanges.last_7_days,
        dateRanges.last_14_days,
        dateRanges.last_30_days,
      ],
      defaultPeriod: dateRanges.last_30_days,
      dateRanges,
    };
  }, []);

  const [periodLabel, setPeriodLabel] = useState<string>(periods.defaultPeriod.label);
  const [isDateRangeDisabled, setIsDateRangeDisabled] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: periods.defaultPeriod.startDate,
    endDate: periods.defaultPeriod.endDate,
  });
  const { transactionSet, setTransactionSet } = useTransactionSetSelection();

  useEffect(() => {
    setIsDateRangeDisabled(false);
  }, [location.pathname]);

  useEffect(() => {
    const selected = periods.presets.find((v) =>
      v.isSelected(dateRange.startDate, dateRange.endDate),
    );
    const sameYear = isSameDateTime(dateRange.startDate, dateRange.endDate, 'year');
    const df = sameYear ? DateTimeFormat.Date : DateTimeFormat.DateWithYear;
    setPeriodLabel(
      selected
        ? selected.label
        : `${formatDateTime(dateRange.startDate, df)} - ${formatDateTime(dateRange.endDate, df)}`,
    );
  }, [dateRange, periods]);

  const tabItems: PerformanceTabItem[] = useMemo(() => {
    if (isTobaccoPerformanceEnabled) {
      return [LOYALTY_PROGRAM_TAB, TOBACCO_PROGRAM_TAB];
    }

    return [LOYALTY_PROGRAM_TAB];
  }, [isTobaccoPerformanceEnabled]);

  const currentTabConfig =
    tabItems.find((tab) => location.pathname.includes(tab.value)) ?? tabItems[0];
  const isLoyaltyTab = currentTabConfig.value === LOYALTY_PROGRAM_TAB.value;

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        titleTestId: 'rc-performance-title',
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <>
          {isMobilePayMetricsEnabled && isLoyaltyTab && (
            <TransactionSetSelector
              value={transactionSet}
              onChange={setTransactionSet}
              variant='onDark'
            />
          )}
          <DateRangeSelector
            value={dateRange}
            onChange={(value) => setDateRange(value)}
            dateRangePresets={periods.presets}
            disabled={isDateRangeDisabled}
          />
        </>
      }
    >
      <Tabs value={currentTabConfig.value} color='secondary'>
        {tabItems.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            component={NavLink}
            to={tab.value}
          />
        ))}
      </Tabs>

      <Routes>
        <Route
          path={`/${LOYALTY_PROGRAM_TAB_PATH satisfies TabValue}`}
          element={
            <RetailerNetworkLoyaltyPerformanceContainer
              retailerNetworkUuid={retailerNetwork.uuid}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              periodLabel={periodLabel}
              mobilePayFilter={transactionSet}
            />
          }
        />

        {isTobaccoPerformanceEnabled && (
          <Route
            path={`/${TOBACCO_PROGRAM_TAB_PATH satisfies TabValue}`}
            element={
              <RetailerNetworkTobaccoPerformanceController
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                retailerNetworkUuid={retailerNetwork.uuid}
                onZeroData={() => setIsDateRangeDisabled(true)}
              />
            }
          />
        )}

        <Route path='*' element={<Navigate to={LOYALTY_PROGRAM_TAB_PATH} replace />} />
      </Routes>
    </Page>
  );
};

export default withRetailerNetwork(RetailerNetworkPerformancePage);
